import React, { useState, useRef, useEffect } from 'react';
import api from '../utils/api';
import './WaiverModal.css';

const WaiverModal = ({ user, allPlayers, onComplete, onLogout }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signature, setSignature] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [adultPlayers, setAdultPlayers] = useState([]);
  const [minorPlayers, setMinorPlayers] = useState([]);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    // Calculate age and separate adult vs minor players
    const now = new Date();
    const adults = [];
    const minors = [];

    // Login user is always considered as adult
    const loginUserName = `${user.firstName} ${user.lastName}`;
    
    console.log('WAIVER: Processing players:', allPlayers);
    console.log('WAIVER: Login user:', loginUserName);
    
    allPlayers.forEach(player => {
      console.log('WAIVER: Processing player:', player);
      console.log('WAIVER: Player DOB:', player.date_of_birth, 'DOB field:', player.dateOfBirth);
      
      const dob = player.date_of_birth || player.dateOfBirth;
      
      if (dob) {
        const birthDate = new Date(dob);
        const age = now.getFullYear() - birthDate.getFullYear();
        const monthDiff = now.getMonth() - birthDate.getMonth();
        const actualAge = (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) ? age - 1 : age;
        
        const playerName = `${player.first_name} ${player.last_name}`;
        
        console.log(`WAIVER: ${playerName} - Birth: ${dob}, Age: ${actualAge}`);
        
        if (actualAge >= 18) {
          // Add to adults if not the login user (login user added separately)
          if (playerName !== loginUserName) {
            console.log(`WAIVER: Adding ${playerName} to ADULTS`);
            adults.push(playerName);
          }
        } else {
          console.log(`WAIVER: Adding ${playerName} to MINORS`);
          minors.push(playerName);
        }
      } else {
        console.warn('WAIVER: Player has no date_of_birth:', player);
      }
    });

    console.log('WAIVER: Final adults:', [loginUserName, ...adults]);
    console.log('WAIVER: Final minors:', minors);

    // Login user is always first in adult list
    setAdultPlayers([loginUserName, ...adults]);
    setMinorPlayers(minors);
  }, [allPlayers, user]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#1e40af';
    }
  }, [step]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    setIsDrawing(true);
    const x = e.clientX ? e.clientX - rect.left : e.touches[0].clientX - rect.left;
    const y = e.clientY ? e.clientY - rect.top : e.touches[0].clientY - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    const x = e.clientX ? e.clientX - rect.left : e.touches[0].clientX - rect.left;
    const y = e.clientY ? e.clientY - rect.top : e.touches[0].clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleNext = () => {
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!acknowledged) {
      setError('Please acknowledge that you have read and agree to the waiver');
      return;
    }

    if (!hasSignature) {
      setError('Please provide your signature');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');

      const response = await api.post('/api/submit-waiver', {
        adult_players: adultPlayers.join(', '),
        minor_players: minorPlayers.length > 0 ? minorPlayers.join(', ') : null,
        guardian_name: `${user.firstName} ${user.lastName}`,
        signature: signatureData,
        acknowledged: acknowledged
      });

      if (response.data.success) {
        onComplete();
      }
    } catch (err) {
      console.error('Waiver submission error:', err);
      setError(err.response?.data?.error || 'Failed to submit waiver. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getWaiverContent = () => {
    return (
      <div className="waiver-content-scroll">
        {/* ADULT WAIVER - Always shown (logged-in user is always adult) */}
        <div className="waiver-section">
          <h2 style={{textAlign: 'center', marginBottom: '20px'}}>AMATEUR ATHLETIC WAIVER AND RELEASE OF LIABILITY</h2>
          
          <h3 style={{textDecoration: 'underline'}}>READ BEFORE SIGNING</h3>
          
          <p>In consideration of being allowed to participate in any way in Redmond Cow Corner LLC (DBA: MSK Prasad's International Cricket Academy - Seattle) athletic sports program, related events and activities, the undersigned acknowledges, appreciates, and agrees that:</p>
          
          <ol style={{marginLeft: '20px'}}>
            <li style={{marginBottom: '15px'}}>
              The risks of injury and illness (ex: communicable diseases such as MRSA, influenza, and COVID-19) from the activities involved in this program are significant, including the potential for permanent paralysis and death, and while particular rules, equipment, and personal discipline may reduce these risks, the risks of serious injury and illness do exist; and,
            </li>
            
            <li style={{marginBottom: '15px'}}>
              I KNOWINGLY AND FREELY ASSUME ALL SUCH RISKS, both known and unknown, EVEN IF ARISING FROM THE NEGLIGENCE OF THE RELEASEES or others, and assume full responsibility for my participation; and,
            </li>
            
            <li style={{marginBottom: '15px'}}>
              I willingly agree to comply with the stated and customary terms and conditions for participation. If, however, I observe any unusual significant hazard during my presence or participation, I will remove myself from participation and bring such to the attention of the nearest official immediately; and,
            </li>
            
            <li style={{marginBottom: '15px'}}>
              I, for myself, my heirs, assigns, personal representatives and next of kin, HEREBY RELEASE AND HOLD HARMLESS Redmond Cow Corner LLC (DBA: MSK Prasad's International Cricket Academy - Seattle) their officers, officials, agents, and/or employees, other participants, sponsoring agencies, sponsors, advertisers, and if applicable, owners and lessors of premises used to conduct the event ("RELEASEES"), WITH RESPECT TO ANY AND ALL INJURY, ILLNESS, DISABILITY, DEATH, or loss or damage to person or property, WHETHER ARISING FROM THE NEGLIGENCE OF THE RELEASEES OR OTHERWISE, to the fullest extent permitted by law.
            </li>
          </ol>
          
          <p style={{fontWeight: 'bold', marginTop: '20px'}}>
            I HAVE READ THIS RELEASE OF LIABILITY AND ASSUMPTION OF RISK AGREEMENT, FULLY UNDERSTAND ITS TERMS, UNDERSTAND THAT I HAVE GIVEN UP SUBSTANTIAL RIGHTS BY SIGNING IT, AND SIGN IT FREELY AND VOLUNTARILY WITHOUT ANY INDUCEMENT.
          </p>
          
          <div style={{marginTop: '30px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px'}}>
            <p style={{marginBottom: '10px'}}><strong>Adult Participants:</strong></p>
            <p style={{fontSize: '16px', color: '#1e40af'}}>{adultPlayers.join(', ')}</p>
          </div>
        </div>

        {/* MINOR WAIVER - Only show if there are minors */}
        {minorPlayers.length > 0 && (
          <div className="waiver-section" style={{marginTop: '40px', borderTop: '2px solid #e5e7eb', paddingTop: '30px'}}>
            <h2 style={{textAlign: 'center', marginBottom: '20px'}}>RELEASE OF LIABILITY FOR MINOR PARTICIPANTS</h2>
            
            <h3 style={{textDecoration: 'underline'}}>READ BEFORE SIGNING</h3>
            
            <p>IN CONSIDERATION OF my child/ward being allowed to Name of Minor Child/Ward participate in any way in the Redmond Cow Corner LLC (DBA: MSK Prasad's International Cricket Academy - Seattle) related events and activities, the undersigned acknowledges, appreciates, and agrees that:</p>
            
            <p style={{marginTop: '15px'}}>
              The risks of injury and illness (ex: communicable diseases such as MRSA, influenza, and COVID-19) to my child from the activities involved in these programs are significant, including the potential for permanent disability and death, and while particular rules, equipment, and personal discipline may reduce these risks, the risks of serious injury and illness do exist; and,
            </p>
            
            <ol style={{marginLeft: '20px', marginTop: '15px'}}>
              <li style={{marginBottom: '15px'}}>
                FOR MYSELF, SPOUSE, AND CHILD, I KNOWINGLY AND FREELY ASSUME ALL SUCH RISKS, both known and unknown, EVEN IF ARISING FROM THE NEGLIGENCE OF THE RELEASEES or others, and assume full responsibility for my child's participation; and,
              </li>
              
              <li style={{marginBottom: '15px'}}>
                I willingly agree to comply with the stated and customary terms and conditions for participation. If I observe any unusual significant concern in my child's readiness for participation and/or in the program itself, I will remove my child from the program and bring such to the attention of the nearest official immediately; and,
              </li>
              
              <li style={{marginBottom: '15px'}}>
                I myself, my spouse, my child, and on behalf of my/our heirs, assigns, personal representatives and next of kin, HEREBY RELEASE AND HOLD HARMLESS Redmond Cow Corner LLC (DBA: MSK Prasad's International Cricket Academy - Seattle), its directors, officers, officials, agents, employees, volunteers, other participants, sponsoring agencies, sponsors, advertisers, and if applicable, owners and lessors of premises used to conduct the event ("Releasees"), WITH RESPECT TO ANY AND ALL INJURY, ILLNESS, DISABILITY, DEATH, or loss or damage to person or property incident to my child's involvement or participation in these programs, WHETHER ARISING FROM THE NEGLIGENCE OF THE RELEASEES OR OTHERWISE, to the fullest extent permitted by law.
              </li>
              
              <li style={{marginBottom: '15px'}}>
                I, for myself, my spouse, my child, and on behalf of my/our heirs, assigns, personal representatives and next of kin, HEREBY INDEMNIFY AND HOLD HARMLESS all the above Releasees from any and all liabilities incident to my child's involvement or participation in these programs, EVEN IF ARISING FROM THEIR NEGLIGENCE, to the fullest extent permitted by law.
              </li>
              
              <li style={{marginBottom: '15px'}}>
                I, the parent/guardian, assert that I have explained to my child/ward: the risks of the activity, his/her responsibilities for adhering to the rules and regulations, and that my child/ward understands this agreement.
              </li>
            </ol>
            
            <p style={{fontWeight: 'bold', marginTop: '20px'}}>
              I, FOR MYSELF, MY SPOUSE, AND CHILD/WARD, HAVE READ THIS RELEASE OF LIABILITY AND ASSUMPTION OF RISK AGREEMENT, FULLY UNDERSTAND ITS TERMS, UNDERSTAND THAT WE HAVE GIVEN UP SUBSTANTIAL RIGHTS BY SIGNING IT, AND SIGN IT FREELY AND VOLUNTARILY WITHOUT ANY INDUCEMENT.
            </p>
            
            <div style={{marginTop: '30px', padding: '15px', backgroundColor: '#fef3c7', borderRadius: '8px'}}>
              <p style={{marginBottom: '10px'}}><strong>Minor Participants:</strong></p>
              <p style={{fontSize: '16px', color: '#92400e'}}>{minorPlayers.join(', ')}</p>
              <p style={{marginTop: '10px'}}><strong>Parent/Guardian:</strong> {user.firstName} {user.lastName}</p>
            </div>
            
            <div style={{marginTop: '30px', padding: '15px', backgroundColor: '#f3f4f6', borderRadius: '8px'}}>
              <h4 style={{marginBottom: '10px'}}>UNDERSTANDING OR RISK</h4>
              <p>I understand the seriousness of the risks involved in participating in this program, my personal responsibilities for adhering to rules and regulation, and accept them as a participant.</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="waiver-modal-overlay">
      <div className="waiver-modal">
        <div className="waiver-header">
          <h2>🏏 Waiver Agreement Required</h2>
          <p>Please review and sign the waiver to continue</p>
        </div>

        {error && (
          <div className="waiver-error">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {step === 1 && (
          <div className="waiver-step">
            {getWaiverContent()}

            <div className="waiver-actions" style={{marginTop: '20px'}}>
              <button className="btn-secondary" onClick={onLogout}>
                Cancel & Logout
              </button>
              <button className="btn-primary" onClick={handleNext}>
                Next: Sign Waiver
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="waiver-step">
            <div className="signature-section">
              <h4>Digital Signature</h4>
              <p className="signature-instruction">
                By signing below, you acknowledge that you have read, understood, and agree to the terms of the waiver(s) above for all participants listed.
              </p>
              
              <div className="signature-canvas-container">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="signature-canvas"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                {!hasSignature && (
                  <div className="signature-placeholder">Sign here</div>
                )}
              </div>
              
              <button 
                className="btn-clear" 
                onClick={clearSignature}
                type="button"
              >
                Clear Signature
              </button>
            </div>

            <div className="acknowledgment-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <span>
                  I, <strong>{user.firstName} {user.lastName}</strong>, acknowledge that I have read and understood the waiver(s) above. 
                  I agree to their terms and sign freely and voluntarily as the participant/parent/guardian for all listed individuals.
                </span>
              </label>
            </div>

            <div className="waiver-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setStep(1)}
                disabled={loading}
              >
                Back
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSubmit}
                disabled={loading || !acknowledged || !hasSignature}
              >
                {loading ? 'Submitting...' : 'Submit & Continue'}
              </button>
            </div>
          </div>
        )}

        <div className="waiver-footer">
          <p>
            <strong>Note:</strong> You must complete this waiver to access your account. 
            If you choose to logout without completing the waiver, you will be required 
            to complete it upon your next login.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaiverModal;
